import type { ApprovalView } from "@mailhand/core/api";
import { type KeyboardEvent, useRef, useState } from "react";
import { Link } from "../../app/router.js";
import { shortTime } from "../../lib/format.js";
import { Badge, Button, Icon, Textarea } from "../../ui/index.js";
import { InputValueList } from "../activity/RunList.js";
import { OriginalMessage } from "../shared/OriginalMessage.js";

interface ApprovalCardProps {
  approval: ApprovalView;
  busy: boolean;
  now: Date;
  /** `edits` holds only the written inputs the member changed; empty approves the draft as is. */
  onApprove: (edits: Record<string, string>) => void;
  onReject: () => void;
  onDismiss: () => void;
  onChangeAction: () => void;
}

type Shortcut = "approve" | "reject" | "dismiss" | "changeAction" | "edit";

const SHORTCUTS: Record<string, Shortcut> = {
  a: "approve",
  r: "reject",
  d: "dismiss",
  c: "changeAction",
  e: "edit",
};

/**
 * One waiting run. The text the writer drafted is editable in place. With the
 * card focused, `a` approves, `r` rejects, `d` dismisses, `c` changes the action, and `e`
 * edits; inside the text, Cmd/Ctrl+Enter approves and Escape goes back to the card.
 */
export function ApprovalCard(props: ApprovalCardProps) {
  const { approval, busy, now, onApprove, onReject, onDismiss, onChangeAction } = props;
  const card = useRef<HTMLElement>(null);
  const firstField = useRef<HTMLTextAreaElement>(null);
  const draft = (name: string): string => String(approval.inputs[name] ?? "");
  const [texts, setTexts] = useState<Record<string, string>>(() => Object.fromEntries(approval.editable.map((name) => [name, draft(name)])));

  const fixed = Object.fromEntries(Object.entries(approval.inputs).filter(([name]) => !approval.editable.includes(name)));
  const edits = Object.fromEntries(Object.entries(texts).filter(([name, text]) => text !== draft(name)));
  const edited = Object.keys(edits).length > 0;
  const blank = Object.values(texts).some((text) => text.trim() === "");

  function approve(): void {
    if (busy || blank) return;
    onApprove(Object.fromEntries(Object.entries(edits).map(([name, text]) => [name, text.trim()])));
  }

  function onCardKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.target !== event.currentTarget || event.metaKey || event.ctrlKey || event.altKey || busy) return;
    const shortcut = SHORTCUTS[event.key.toLowerCase()];
    if (shortcut === undefined) return;
    event.preventDefault();
    if (shortcut === "approve") approve();
    else if (shortcut === "reject") onReject();
    else if (shortcut === "dismiss") onDismiss();
    else if (shortcut === "changeAction") onChangeAction();
    else firstField.current?.focus();
  }

  function onFieldKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      approve();
    } else if (event.key === "Escape") {
      event.preventDefault();
      card.current?.focus();
    }
  }

  return (
    <article ref={card} className="card card-padded approval" tabIndex={0} onKeyDown={onCardKeyDown} aria-label={`${approval.actionName} for ${approval.from}: ${approval.subject}`}>
      <div className="approval-head">
        <div>
          <div className="approval-from">
            <strong>{approval.from}</strong>
            <span className="mono muted small">{shortTime(approval.createdAt, now)}</span>
          </div>
          <Link to={`/messages/${approval.messageId}`} className="approval-subject">
            {approval.subject === "" ? "(no subject)" : approval.subject}
          </Link>
        </div>
        <Badge tone="amber" dot>
          {approval.actionName}
        </Badge>
      </div>
      <section className="approval-original" aria-label="Their message">
        <span className="field-label">Their message</span>
        <OriginalMessage messageId={approval.messageId} className="message-text-short" />
      </section>
      {Object.keys(fixed).length === 0 ? null : <InputValueList values={fixed} />}
      {approval.editable.map((name, index) => (
        <div key={name} className="approval-edit">
          <div className="approval-edit-head">
            <label className="field-label" htmlFor={`${approval.runId}-${name}`}>
              {name}
            </label>
            {texts[name] === draft(name) ? null : (
              <>
                <Badge tone="blue">Edited</Badge>
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => { setTexts({ ...texts, [name]: draft(name) }); }}>
                  Reset to draft
                </Button>
              </>
            )}
          </div>
          <Textarea
            id={`${approval.runId}-${name}`}
            ref={index === 0 ? firstField : undefined}
            className="approval-text"
            value={texts[name] ?? ""}
            disabled={busy}
            rows={Math.max(3, (texts[name] ?? "").split("\n").length + 1)}
            onChange={(event) => { setTexts({ ...texts, [name]: event.target.value }); }}
            onKeyDown={onFieldKeyDown}
          />
        </div>
      ))}
      <div className="button-row">
        <Button variant="accent" busy={busy} disabled={blank} onClick={approve}>
          <Icon name="check" size={15} /> {edited ? "Approve Edited Version" : "Approve"} <kbd>a</kbd>
        </Button>
        <Button onClick={onReject} disabled={busy}>
          Reject <kbd>r</kbd>
        </Button>
        <Button onClick={onDismiss} disabled={busy}>
          Dismiss <kbd>d</kbd>
        </Button>
        <Button variant="ghost" onClick={onChangeAction} disabled={busy}>
          Change action <kbd>c</kbd>
        </Button>
        {approval.editable.length === 0 ? null : (
          <span className="muted small">
            <kbd>e</kbd> edit · <kbd>⌘↵</kbd> approve
          </span>
        )}
      </div>
    </article>
  );
}
